/**
 * Microphone permission helpers (FLO-109).
 *
 * Mobile browsers — iOS Safari in particular — only surface the mic permission
 * prompt when `getUserMedia()` is reached during a *transient user activation*
 * (a real tap/click). Calling it at page load, or after a network round-trip
 * that outlives the activation window, silently fails: the prompt never shows
 * and voice can't start. These helpers keep the permission *decisions* pure and
 * unit-testable; the gesture wiring (where/when getUserMedia is actually called)
 * lives in OnboardingSDK.
 */

/** How a getUserMedia / initDevices rejection should be interpreted. */
export type MicErrorKind = 'permission-denied' | 'no-device' | 'other';

/**
 * Read the DOMException-style `.name` off an error, tolerating wrapped errors.
 * Some WebRTC transports re-wrap a DOMException before rethrowing, so we can't
 * rely on `instanceof DOMException` alone.
 */
export function micErrorName(error: unknown): string | null {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name;
  }
  if (error !== null && typeof error === 'object') {
    const name = (error as { name?: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return null;
}

/**
 * Classify a getUserMedia / initDevices rejection.
 *
 *  - `permission-denied` — user denied or the prompt was blocked/cancelled.
 *    iOS Safari throws `NotAllowedError`; `SecurityError` covers policy blocks
 *    (insecure context, Permissions-Policy); `AbortError` covers a dismissed
 *    prompt.
 *  - `no-device` — no usable microphone hardware. Android Chrome throws
 *    `NotFoundError` when there's no input device, `NotReadableError` when the
 *    OS won't release it, `OverconstrainedError` when constraints can't be met.
 *  - `other` — anything else (network, abort-by-us, unknown).
 *
 * Both `permission-denied` and `no-device` mean voice can't run → the caller
 * should fall back to text mode. The split exists so we can log/explain the
 * difference (iOS deny vs Android no-mic).
 */
export function classifyMicError(error: unknown): MicErrorKind {
  const name = micErrorName(error);
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'AbortError':
      return 'permission-denied';
    case 'NotFoundError':
    case 'NotReadableError':
    case 'OverconstrainedError':
      return 'no-device';
    default:
      return 'other';
  }
}

/** Decision derived from a Permissions API pre-check. */
export type MicPrecheck = 'granted' | 'denied' | 'prompt';

/**
 * Map a `PermissionState` (from `navigator.permissions.query({name:'microphone'})`)
 * to our pre-check decision:
 *
 *  - `granted` → permission already held; `getUserMedia` won't prompt.
 *  - `denied`  → the browser will NOT show a prompt; skip `getUserMedia` and go
 *               straight to text mode (firing it would just reject).
 *  - `prompt`  → must call `getUserMedia` from the gesture to surface the prompt.
 *
 * Anything unexpected (undefined / unsupported state) is treated as `prompt`,
 * because `getUserMedia` is the source of truth.
 */
export function micPrecheckFromState(state: string | undefined | null): MicPrecheck {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'prompt';
}

/**
 * Outcome of a microphone probe.
 *
 * `granted` is the only thing that authorizes unmuting. `denied` is narrower —
 * true ONLY when the visitor refused the prompt — so UI copy can distinguish
 * "you said no" from "there's no usable microphone here"; both leave
 * `granted: false`.
 */
export interface MicProbeResult {
  granted: boolean;
  denied: boolean;
  /** How the rejection was classified. Absent when the probe succeeded. */
  kind?: MicErrorKind;
}

/**
 * What an AUTOMATIC (non-gesture) mic request is allowed to conclude.
 *
 * FLO-786: the website agent asks for the mic itself once its spoken welcome
 * ends, which is seconds after the launcher tap — the transient user activation
 * is long gone. Chrome and Firefox still show the prompt; WebKit does not, and
 * rejects with the SAME `NotAllowedError` the visitor's own "Block" produces.
 * Committing that as a denial would tell a Safari visitor their mic is blocked
 * in browser settings when nothing was ever asked.
 *
 * So a permission-shaped failure is only believed when the Permissions API
 * independently confirms `denied`. Anything else is `inconclusive`: nothing is
 * recorded, and the caller falls back to the tap-driven invitation, which is a
 * real gesture and works everywhere.
 *
 *  - `granted`      — permission held; unmute.
 *  - `failed`       — a real, committable failure (confirmed denial, or no
 *                     usable device, which no gesture would fix).
 *  - `inconclusive` — the browser never took a decision. Ask again from a tap.
 */
export type AutoMicOutcome = 'granted' | 'failed' | 'inconclusive';

export function classifyAutoMicProbe(
  result: MicProbeResult,
  permission: MicPrecheck,
): AutoMicOutcome {
  if (result.granted) return 'granted';
  if (result.kind === 'permission-denied' && permission !== 'denied') {
    return 'inconclusive';
  }
  return 'failed';
}

/**
 * The `PermissionStatus` shape this SDK actually relies on.
 *
 * Structural rather than the DOM lib's `PermissionStatus` because
 * `navigator.permissions.query({name:'microphone'})` is not typed at all —
 * `PermissionName` omits `'microphone'` — and because the event-target half is
 * optional in practice: a browser can answer the query and still not deliver
 * `change` events, which must degrade to "no live recovery", not a throw.
 */
export interface MicPermissionStatus {
  state: string;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

/** Minimal Pipecat shape needed to reconcile a gesture captured during connect. */
export interface MicToggleClient {
  readonly isMicEnabled: boolean;
  enableMic(enabled: boolean): void;
}

/**
 * Re-apply a granted Website Agent voice intent at the transport boundary.
 *
 * The visitor can grant access after the client object exists but before Daily
 * finishes joining. An early enableMic call is not guaranteed to survive that
 * transition, so onConnected calls this once more. The desired state remains
 * authoritative after the setter because the transport getter can lag behind
 * it. A false desired state is equally important: an explicit mute during
 * connection must never be overwritten when the transport comes online.
 * Non-Website-Agent callers pass null and simply observe the transport state.
 */
export function reconcileMicIntentOnConnect(
  client: MicToggleClient,
  desiredState: boolean | null,
): boolean {
  if (desiredState === null) return client.isMicEnabled;
  if (client.isMicEnabled !== desiredState) {
    client.enableMic(desiredState);
  }
  return desiredState;
}

/**
 * During a Website Agent join, the UI's desired state is authoritative: Daily's
 * transport getter may still report its pre-join default. Once connected (and
 * for every other agent), use the transport's actual state.
 */
export function micStateForToggle(options: {
  websiteAgent: boolean;
  connected: boolean;
  desiredState: boolean;
  transportState: boolean;
}): boolean {
  return options.websiteAgent && !options.connected
    ? options.desiredState
    : options.transportState;
}

/** Website Agent has no user waveform, so it must never open an analysis stream. */
export function shouldStartUserMicAnalysis(options: {
  websiteAgent: boolean;
  voicePendingGesture: boolean;
  micEnabled: boolean;
}): boolean {
  return (
    !options.websiteAgent &&
    !options.voicePendingGesture &&
    options.micEnabled
  );
}

/**
 * FLO-786: how long after a refused request an origin-level microphone grant is
 * still read as the visitor answering US.
 *
 * The user story is "the notice says my browser is blocking it, I fix that in
 * the address bar, I come back" — seconds to a minute, generously three. Past
 * that the visitor has moved on, and a grant on this origin is more likely
 * meant for something else on the customer's page than for us.
 */
export const MIC_RECOVERY_WINDOW_MS = 3 * 60_000;

/** Audio constraints used for every mic acquisition (call + visualization). */
export const MIC_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};
